import React, { useState } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { FormGroup, Radio, MenuItem, MenuList, FormControl, Divider, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { TrashIcon } from '@/components/common/Icons/TrashIcon';
import { Button } from '@/components/common/button/button';
import {
    MenuWrapper,
    MenuContent,
    MenuItemText,
    FormControlLabel,
    ButtonWrapper,
    MenuHeader,
    MenuSelectedBadge,
    MenuSelectedAllText,
} from './styles';

interface SingleSelectFilterProps {
    options: { id: string; name: string }[];
    selected: string | null;
    handleChange: (id: string | null) => void;
    label: string;
    prefix?: string;
    disabled?: boolean;
    menuAlignment?: 'left' | 'right';
    showSelectedLabel?: boolean;
}

export const SingleSelectFilter = (props: SingleSelectFilterProps) => {
    const {
        selected,
        options,
        handleChange,
        label,
        prefix,
        disabled,
        menuAlignment = 'right',
        showSelectedLabel = false,
    } = props;
    const selectedOption = options.find((option) => option.id === selected);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setMenuIsOpen(true);
        setMenuAnchor(event.currentTarget);
    };

    const handleClose = () => {
        setMenuAnchor(null);
        setMenuIsOpen(false);
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event.target.value);
        handleClose();
    };

    const handleClear = () => {
        handleChange(null);
    };

    return (
        <React.Fragment>
            <div>
                <ButtonWrapper>
                    <Tooltip title={`${label} selection`}>
                        <Button
                            variant="tertiary"
                            size="small"
                            onClick={handleClick}
                            data-cy={`${prefix}button`}
                            endIcon={menuIsOpen ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                            disabled={disabled}
                        >
                            {showSelectedLabel ? (
                                <>
                                    {label}: {selectedOption ? selectedOption.name : 'All'}
                                </>
                            ) : (
                                <>
                                    <MenuSelectedBadge color="secondary" badgeContent={selected ? 1 : 0} />
                                    <MenuSelectedAllText $isActive={!selected}>All</MenuSelectedAllText> {label}
                                </>
                            )}
                        </Button>
                    </Tooltip>
                </ButtonWrapper>
            </div>

            <MenuWrapper
                anchorEl={menuAnchor}
                id={prefix}
                open={menuIsOpen}
                onClose={handleClose}
                elevation={0}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        mt: '20px',
                    },
                }}
                transformOrigin={{ horizontal: menuAlignment, vertical: 'top' }}
                anchorOrigin={{ horizontal: menuAlignment, vertical: 'bottom' }}
            >
                <MenuContent>
                    <>
                        <MenuHeader>
                            <Typography variant="body2">{label}</Typography>
                            <Button
                                aria-label="Reset selection"
                                onClick={handleClear}
                                startIcon={<TrashIcon />}
                                size="small"
                                variant="text"
                            >
                                clear
                            </Button>
                        </MenuHeader>
                        <Divider />
                        <MenuList sx={{ maxHeight: 500, overflowY: 'scroll' }}>
                            <FormControl component="fieldset" variant="standard">
                                <FormGroup>
                                    {options.map((option) => (
                                        <MenuItem key={option.id}>
                                            <FormControlLabel
                                                control={
                                                    <Radio
                                                        checked={selected === option.id}
                                                        onChange={handleRadioChange}
                                                        value={option.id}
                                                        name={prefix}
                                                        data-cy={prefix ? `${prefix}radio-${option.id}` : undefined}
                                                    />
                                                }
                                                label={<MenuItemText>{option.name}</MenuItemText>}
                                            />
                                        </MenuItem>
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </MenuList>
                    </>
                </MenuContent>
            </MenuWrapper>
        </React.Fragment>
    );
};
